import { useEffect, useMemo } from 'react';
import { Phone, Calendar, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { glossary } from '@/data/utilityPages';
import { practiceAreaLabels } from '@/data/practiceAreas';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';
const URL = '/glossary-of-legal-terms/';
const META_TITLE = 'Glossary of Legal Terms | Morgan Legal Group NYC';
const META_DESC =
  'Plain-English glossary of New York legal terms — estate planning, probate, elder law, family law, real estate and personal injury. Free from Morgan Legal Group.';

function buildSchema() {
  const canonical = `${SITE}${URL}`;
  const dates = pageDates(URL);
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'DefinedTermSet',
      '@id': `${canonical}#glossary`,
      name: 'Morgan Legal Group Glossary of Legal Terms',
      url: canonical,
      hasDefinedTerm: glossary.map((g) => ({
        '@type': 'DefinedTerm',
        name: g.term,
        description: g.definition,
        inDefinedTermSet: `${canonical}#glossary`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Glossary of Legal Terms', item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonical,
      name: META_TITLE,
      description: META_DESC,
      datePublished: dates.published,
      dateModified: dates.modified,
    },
  ];
}

export default function GlossaryPage() {
  const schema = buildSchema();

  useEffect(() => {
    const canonical = `${SITE}${URL}`;
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: META_TITLE, description: META_DESC, url: canonical });
    }
  }, []);

  const grouped = useMemo(() => {
    const sorted = [...glossary].sort((a, b) => a.term.localeCompare(b.term));
    const buckets: Record<string, typeof glossary> = {};
    for (const g of sorted) {
      const letter = g.term[0].toUpperCase();
      if (!buckets[letter]) buckets[letter] = [];
      buckets[letter].push(g);
    }
    return buckets;
  }, []);

  const letters = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="glossary-page">
        <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span
              className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
              style={{ opacity: 0.03 }}
            >
              GLOSSARY
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">Glossary of Legal Terms</span>
            </nav>
            <div className="max-w-3xl">
              <div className="section-label mb-5 text-gold">Plain-English Reference</div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-7">
                Glossary of <span className="text-gold italic">Legal Terms</span>
              </h1>
              <p className="lead font-body text-white/80 text-lg lg:text-xl leading-relaxed mb-8 max-w-2xl" data-speakable>
                A working reference of terms that come up in estate planning, probate, elder law, family law, real estate,
                and personal injury work — written in plain English, with statutory citations where they
                matter.
              </p>
              <nav aria-label="Glossary jumplinks" className="flex flex-wrap gap-2">
                {letters.map((L) => (
                  <a
                    key={L}
                    href={`#letter-${L}`}
                    className="inline-flex items-center justify-center w-8 h-8 border border-gold/40 text-gold font-display font-bold text-sm hover:bg-gold hover:text-navy transition-colors"
                  >
                    {L}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-cream">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {letters.map((L) => (
              <div key={L} id={`letter-${L}`} className="mb-14 lg:mb-20 scroll-mt-24">
                <div className="flex items-baseline gap-5 mb-7">
                  <span className="font-display text-gold text-5xl lg:text-6xl font-bold">{L}</span>
                  <span className="flex-1 h-px bg-navy/15" />
                </div>
                <dl className="space-y-7">
                  {grouped[L].map((g) => (
                    <div key={g.term} className="grid grid-cols-1 md:grid-cols-12 gap-3 lg:gap-6">
                      <dt className="md:col-span-4">
                        <h2 className="font-display text-navy text-lg lg:text-xl font-bold leading-tight">{g.term}</h2>
                      </dt>
                      <dd className="md:col-span-8 font-body text-navy/75 text-base lg:text-lg leading-relaxed">
                        {g.definition}
                        {g.relatedSlugs && g.relatedSlugs.length ? (
                          <span className="block mt-2 text-xs uppercase tracking-wider">
                            {g.relatedSlugs.map((s, i) => (
                              <span key={s}>
                                {i > 0 ? ' · ' : ''}
                                <a href={`/${s}/`} className="text-gold hover:underline">
                                  {practiceAreaLabels[s]?.label ?? s}
                                </a>
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gold py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              Question About a Specific Term?
            </h2>
            <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              The glossary is intentionally short. For application of a term to your specific situation — what an
              equitable distribution looks like in YOUR divorce, or whether a MAPT fits YOUR family — book a free
              consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/appointment/" className="btn-navy">
                <Calendar className="w-4 h-4" />
                Schedule Free Consultation
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="tel:8885291315"
                className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
              >
                <Phone className="w-4 h-4" />
                888-529-1315
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
